import type { ApiListResponse } from "@academia-hub/shared-types";
import { apiGetList } from "./api-client.js";

/** The API's largest page (`paginationQuerySchema`'s `limit` max). */
export const MAX_PAGE_SIZE = 200;

/**
 * Every row of a paged list endpoint, for a list that must be complete (a picker, or a check that
 * looks at every row) — never a silently capped first page. `fetchPage` requests one page at
 * `MAX_PAGE_SIZE`; the remaining pages are fetched in parallel once the first reports the total.
 */
export async function fetchAllPages<T>(fetchPage: (page: number) => Promise<ApiListResponse<T>>): Promise<ApiListResponse<T>> {
  const first = await fetchPage(1);
  const rest = await Promise.all(Array.from({ length: Math.max(first.meta.totalPages - 1, 0) }, (_, index) => fetchPage(index + 2)));
  const data = [first, ...rest].flatMap((body) => body.data);
  return { data, meta: { page: 1, limit: Math.max(data.length, 1), total: data.length, totalPages: 1 } };
}

/**
 * `fetchAllPages` for a list fetcher: every row of `GET path?query`, at `MAX_PAGE_SIZE` a page. For the
 * lists pickers and lookups read whole (a school's sections, fee heads, stores, …): the API's default
 * page is 20, and a fixed `limit` only moves the cliff.
 */
export function fetchAllRows<T>(path: string, query: URLSearchParams = new URLSearchParams()): Promise<ApiListResponse<T>> {
  return fetchAllPages((page) => {
    const pageQuery = new URLSearchParams(query);
    pageQuery.set("limit", String(MAX_PAGE_SIZE));
    pageQuery.set("page", String(page));
    return apiGetList<T>(`${path}?${pageQuery.toString()}`);
  });
}
